import { lazy, Suspense, useReducer } from "react";
import { useTranslation } from "@repo/i18n";
import type { BookingFlowContext } from "./context";
import { resolveBookingConfig } from "../config/types";
import { todayInTz } from "./format";
import { flowReducer, initialFlowState } from "./flow/state";
import { Search } from "./steps/Search";
import { Slots } from "./steps/Slots";
import { Details } from "./steps/Details";
import { Blocked, Confirmed, ContactFallback, Pending } from "./steps/EndScreens";

/**
 * The SAQ-A guarantee card step pulls in Stripe (@repo/payments). It is reached ONLY when a service
 * requires a card guarantee — a rare branch — so it is code-split: the common diner path never loads
 * Stripe.js, keeping the embed diner-weight (widget-embed-shell).
 */
const GuaranteeStep = lazy(() => import("./steps/GuaranteeStep"));

/**
 * The diner booking journey — the real body of the widget's mount seam. Owns the flow machine
 * (`useReducer`) seeded with the resolved `venueId`, and renders exactly one step at a time. Everything
 * it needs (venue config, booking params, the canonical `BookingChannel`, the active locale) comes from
 * the shell's `BookingFlowContext`. Single-column, mobile-first, FR/EN; no router (the host owns URL).
 */
export function BookingJourney({ context }: { context: BookingFlowContext }) {
  const config = context.venueConfig;
  const booking = resolveBookingConfig(config);
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  // Seed the date with today in the venue timezone (the picker's `min`) so the diner lands on a
  // ready-to-search form instead of an empty date field.
  const [state, dispatch] = useReducer(flowReducer, config, (c) =>
    initialFlowState(c.venueId, 2, todayInTz(c.timeZone)),
  );

  switch (state.step) {
    case "SEARCH":
      return (
        <Search state={state} dispatch={dispatch} booking={booking} timeZone={config.timeZone} />
      );
    case "SLOTS":
      return <Slots state={state} dispatch={dispatch} config={config} booking={booking} />;
    case "DETAILS":
      return (
        <Details
          state={state}
          dispatch={dispatch}
          config={config}
          booking={booking}
          channel={context.bookingChannel}
          locale={locale}
          preview={context.preview}
        />
      );
    case "GUARANTEE":
      return (
        <Suspense
          fallback={
            <output className="rsv-hint" aria-live="polite">
              {t("widget.loading")}
            </output>
          }
        >
          <GuaranteeStep
            guarantee={state.guarantee}
            config={config}
            dispatch={dispatch}
            locale={locale}
          />
        </Suspense>
      );
    case "CONFIRMED":
      return (
        <Confirmed
          confirmation={state.confirmation}
          config={config}
          locale={locale}
          preview={context.preview}
          serviceDay={confirmationServiceDay(state.confirmation.startSec, config.timeZone)}
        />
      );
    case "PENDING":
      return (
        <Pending
          confirmation={state.confirmation}
          config={config}
          locale={locale}
          preview={context.preview}
          serviceDay={confirmationServiceDay(state.confirmation.startSec, config.timeZone)}
        />
      );
    case "BLOCKED":
      return <Blocked config={config} />;
    case "CONTACT_FALLBACK":
      return <ContactFallback config={config} rejectionCode={state.rejectionCode} />;
  }
}

/** The venue-local `YYYY-MM-DD` an end-screen slot falls on (the reducer only carries the instant). */
function confirmationServiceDay(startSec: number, timeZone: string): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(startSec * 1000));
  const map: Record<string, string> = {};
  for (const p of parts) map[p.type] = p.value;
  return `${map.year}-${map.month}-${map.day}`;
}
